import { useCreatePayPoint } from "Api/Mutations/PayPoints/useCreatePayPoint";
import { useUpdatePayPoint } from "Api/Mutations/PayPoints/useUpdatePayPoint";
import {
  isRedirectPaymentType,
  toPayPointSettingsDto,
} from "Components/Paypoints/paymentType";
import {
  type AdvancedFieldsVariant,
  PaypointAccessSection,
} from "Components/Paypoints/PaypointAccessSection";
import { PaypointActivationSection } from "Components/Paypoints/PaypointActivationSection";
import { PaypointDeviceSection } from "Components/Paypoints/PaypointDeviceSection";
import { PaypointGatewaySection } from "Components/Paypoints/PaypointGatewaySection";
import { PaypointGeneralInfoSection } from "Components/Paypoints/PaypointGeneralInfoSection";
import { PaypointNfcSection } from "Components/Paypoints/PaypointNfcSection";
import { PaypointPaymentSettingsSection } from "Components/Paypoints/PaypointPaymentSettingsSection";
import {
  getPaypointSchema,
  type PaypointFormData,
} from "Components/Paypoints/schema";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import {
  isComgateProductCode,
  isGatewayProductCode,
} from "Components/Shared/Merchant/Products/AppProducts/constants";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import {
  type ApiProblemDetails,
  type BankAccountListItem,
  type EmployeeDto,
  type ErrorResponse,
  type MerchantFacilityDto,
  PayPointType,
  type ProductCode,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Paypoint.Form;

const hasNfcCardNoConflict = (error: ErrorResponse) =>
  "data" in error &&
  !!error.data &&
  "errors" in error.data &&
  !!(error.data as ApiProblemDetails).errors?.NFCCardNo;

type Props = {
  defaultValues: Partial<PaypointFormData>;
  facilities: Array<MerchantFacilityDto>;
  accounts: Array<BankAccountListItem>;
  employees: Array<EmployeeDto>;
  bankAccountVariant: AdvancedFieldsVariant;
  employeesVariant: AdvancedFieldsVariant;
  onSuccess: (paypointPublicID: string) => void;
  isBalanceVisible?: boolean;
  merchantPublicID?: string;
  productID?: number | null;
  productCode?: ProductCode | null;
  isTerminalProduct?: boolean;
};

export const PaypointForm = ({
  defaultValues,
  facilities,
  employees,
  accounts,
  bankAccountVariant,
  employeesVariant,
  isBalanceVisible,
  onSuccess,
  merchantPublicID,
  productID,
  productCode,
  isTerminalProduct = false,
}: Props) => {
  const isMerchant = useIsCurrentUserMerchant();
  const isAdmin = useIsCurrentUserAdmin();
  const { t } = useResource();

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setError,
    setValue,
    formState: { isDirty },
  } = useForm<PaypointFormData>({
    resolver: zodResolver(getPaypointSchema(t, isMerchant)),
    defaultValues,
  });

  const { mutate: updatePayPoint, isPending: isPendingUpdate } =
    useUpdatePayPoint({
      onSuccess,
      onError: (error) => {
        if (hasNfcCardNoConflict(error)) {
          toast.error(t(Resources.Hooks.Paypoint.AssignNfcCardNoConflict));
          setError("nfcCardNo", {
            message: t(RESOURCES.AssignNfcCardNoConflict),
          });
        }
      },
    });

  const { mutate: createPayPoint, isPending: isPendingCreate } =
    useCreatePayPoint({
      merchantPublicID,
      onSuccess,
      onError: (error) => {
        if (hasNfcCardNoConflict(error)) {
          toast.error(t(Resources.Hooks.Paypoint.AssignNfcCardNoConflict));
        }
      },
    });

  const onSubmit = (data: PaypointFormData) => {
    const {
      publicID,
      employeePublicIDs,
      paymentType,
      fixedAmount,
      tipsEnabled,
      currencyCode,
      redirectUrl,
      ...rest
    } = data;
    const settings = toPayPointSettingsDto({
      type: rest.type,
      paymentType,
      fixedAmount,
      tipsEnabled,
      currencyCode,
      redirectUrl,
    });

    if (publicID) {
      updatePayPoint({
        data: {
          ...rest,
          publicID,
          employeePublicIDs: employeePublicIDs ?? [],
          settings,
        },
        publicID,
      });
    } else {
      createPayPoint({
        ...rest,
        productID,
        employeePublicIDs: employeePublicIDs ?? [],
        settings,
      });
    }
  };

  const selectedType = watch("type");
  const isGatewayType = selectedType === PayPointType.Gateway;
  // Comgate paypoints are provisioned by the gateway: facility, type and account are fixed.
  const isComgateProduct = isComgateProductCode(productCode);

  // Switching a paypoint to Gateway makes the payment-settings section meaningless
  // (the e-shop drives amount and tips), so reset it back to the neutral default.
  // Redirect modes exist only on NFC tags, so leaving NFC drops them as well.
  // Handled here in the change event, not in a useEffect (see FE conventions).
  const handleTypeChange = (value: string | number) => {
    if (value === PayPointType.Gateway) {
      setValue("paymentType", "Standard");
      setValue("tipsEnabled", false);
    }

    if (
      value !== PayPointType.NFC &&
      isRedirectPaymentType(getValues("paymentType"))
    ) {
      setValue("paymentType", "Standard");
    }
  };

  // A Link target must never show up as the Table service URL, so the URL only
  // survives a switch back to the payment type it was saved with.
  const handlePaymentTypeChange = (value: string) => {
    setValue(
      "redirectUrl",
      value === defaultValues.paymentType ? defaultValues.redirectUrl : null,
    );

    if (isRedirectPaymentType(value)) {
      setValue("tipsEnabled", false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PaypointGeneralInfoSection
        control={control}
        facilities={facilities}
        isGatewayProduct={isGatewayProductCode(productCode)}
        isTerminalProduct={isTerminalProduct}
        isLocked={isComgateProduct}
        onTypeChange={handleTypeChange}
      />
      <PaypointPaymentSettingsSection
        control={control}
        selectedType={selectedType}
        onPaymentTypeChange={handlePaymentTypeChange}
      />
      <PaypointAccessSection
        control={control}
        accounts={accounts}
        employees={employees}
        bankAccountVariant={bankAccountVariant}
        employeesVariant={employeesVariant}
        isBalanceVisible={isBalanceVisible}
        isBankAccountLocked={isComgateProduct}
      />
      {selectedType !== PayPointType.Terminal && !isGatewayType && (
        <PaypointNfcSection
          control={control}
          paypointPublicID={defaultValues.publicID}
          paypointName={defaultValues.name}
        />
      )}
      {selectedType === PayPointType.Terminal &&
        defaultValues.publicID &&
        isMerchant && (
          <PaypointDeviceSection payPointPublicID={defaultValues.publicID} />
        )}
      {selectedType === PayPointType.Terminal &&
        defaultValues.publicID &&
        (isMerchant || isAdmin) && (
          <PaypointActivationSection
            payPointPublicID={defaultValues.publicID}
          />
        )}
      {isGatewayType && defaultValues.publicID && isMerchant && (
        <PaypointGatewaySection payPointPublicID={defaultValues.publicID} />
      )}
      <PrimaryButton
        fullWidth
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isDirty || isPendingUpdate || isPendingCreate}
      >
        {t(Resources.Common.Save)}
      </PrimaryButton>
    </form>
  );
};
